// src/components/login/EnquiryModal.jsx
import { useState, useCallback } from "react";
import "./login.css";

const EnquiryModal = ({ isEnquiryOpen, onCloseEnquiry }) => {
  // ───────── Hooks (always run) ─────────
  const [enquiryData, setEnquiryData] = useState({
    name: "",
    course: "",
    email: "",
    mobile: "",
    tellus: "",
  });
  const [isEnquirySubmitted, setIsEnquirySubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // 'success' | 'error'

  const resetForms = useCallback(() => {
    setEnquiryData({ name: "", course: "", email: "", mobile: "", tellus: "" });
    setMessage("");
    setMessageType("");
    setIsEnquirySubmitted(false);
  }, []);

  const handleEnquiryInputChange = useCallback((field, value) => {
    setEnquiryData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleModalClose = useCallback(
    (closeFn) => {
      resetForms();
      setIsLoading(false);
      closeFn();
    },
    [resetForms],
  );

  const handleOverlayClick = useCallback(
    (closeFn) => (e) => {
      if (e.target === e.currentTarget) handleModalClose(closeFn);
    },
    [handleModalClose],
  );

  const handleContainerClick = useCallback((e) => e.stopPropagation(), []);

  const handleEnquiry = useCallback(
    async (e) => {
      e.preventDefault();
      setIsLoading(true);
      setMessage("");

      try {
        await new Promise((r) => setTimeout(r, 1500));

        if (
          !enquiryData.name ||
          !enquiryData.course ||
          !enquiryData.email ||
          !enquiryData.mobile
        ) {
          throw new Error("Please fill in all required fields");
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(enquiryData.email)) {
          throw new Error("Please enter a valid email address");
        }
        if (enquiryData.mobile.length < 10) {
          throw new Error("Please enter a valid mobile number");
        }

        setIsEnquirySubmitted(true);
        setMessage(
          "Thank you! Your enquiry has been submitted successfully. We'll get back to you soon.",
        );
        setMessageType("success");

        setTimeout(() => {
          resetForms();
          handleModalClose(onCloseEnquiry);
        }, 3000);
      } catch (err) {
        setMessage(err.message || "Failed to submit enquiry. Please try again.");
        setMessageType("error");
      } finally {
        setIsLoading(false);
      }
    },
    [enquiryData, resetForms],
  );

  // ───────── Early return if closed ─────────
  if (!isEnquiryOpen) return null;

  // ───────── Render ─────────
  return (
    <div
      className="modal-overlay enquiry-modal"
      onClick={handleOverlayClick(onCloseEnquiry)}
    >
      <div className="modal-container" onClick={handleContainerClick}>
        <div className="modal-header">
          <button
            className="close-button"
            onClick={() => handleModalClose(onCloseEnquiry)}
          >
            ×
          </button>
          <h2 className="modal-title">Get Started Today</h2>
          <p className="modal-subtitle">
            Tell us about your educational goals
          </p>
        </div>

        <div className="modal-body">
          {isEnquirySubmitted ? (
            <div className="enquiry-success">
              <div className="success-icon">✓</div>
              <h3>Enquiry Submitted!</h3>
              <p>Thank you for your interest. Our team will contact you within 24 hours.</p>
              {message && <div className="success-message">{message}</div>}
            </div>
          ) : (
            <form
              className={`auth-form enquiry-form ${isLoading ? "loading" : ""}`}
              onSubmit={handleEnquiry}
            >
              <div className="form-group">
                <label className="form-label" htmlFor="enquiry-name">
                  Full Name
                </label>
                <input
                  type="text"
                  id="enquiry-name"
                  name="name"
                  className="form-input"
                  placeholder="Enter your full name"
                  value={enquiryData.name}
                  onChange={(e) =>
                    handleEnquiryInputChange("name", e.target.value)
                  }
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="enquiry-course">
                  Course of Interest
                </label>
                <select
                  id="enquiry-course"
                  name="course"
                  className="form-input"
                  value={enquiryData.course}
                  onChange={(e) =>
                    handleEnquiryInputChange("course", e.target.value)
                  }
                  required
                  disabled={isLoading}
                >
                  <option value="">Select a course</option>
                  <option value="university-undergraduate">
                    University - Undergraduate
                  </option>
                  <option value="university-postgraduate">
                    University - Postgraduate
                  </option>
                  <option value="university-phd">
                    University - PhD Programs
                  </option>
                  <option value="skp-foundation">SKP - Foundation Course</option>
                  <option value="skp-diploma">SKP - Diploma Programs</option>
                  <option value="skp-certificate">SKP - Certificate Courses</option>
                  <option value="languages-english">Languages - English</option>
                  <option value="languages-french">Languages - French</option>
                  <option value="languages-german">Languages - German</option>
                  <option value="languages-spanish">Languages - Spanish</option>
                  <option value="ielts-academic">IELTS - Academic</option>
                  <option value="ielts-general">IELTS - General</option>
                  <option value="ielts-preparation">IELTS - Preparation</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="enquiry-email">
                  Email Address
                </label>
                <input
                  type="email"
                  id="enquiry-email"
                  name="email"
                  className="form-input"
                  placeholder="Enter your email address"
                  value={enquiryData.email}
                  onChange={(e) =>
                    handleEnquiryInputChange("email", e.target.value)
                  }
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="enquiry-mobile">
                  Mobile Number
                </label>
                <input
                  type="tel"
                  id="enquiry-mobile"
                  name="mobile"
                  className="form-input"
                  placeholder="Enter your mobile number"
                  value={enquiryData.mobile}
                  onChange={(e) =>
                    handleEnquiryInputChange("mobile", e.target.value)
                  }
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="enquiry-tellus">
                  Tell Us More
                </label>
                <textarea
                  id="enquiry-tellus"
                  name="tellus"
                  className="form-input"
                  placeholder="Tell us about your educational goals, preferred destinations, or any specific questions..."
                  value={enquiryData.tellus}
                  onChange={(e) =>
                    handleEnquiryInputChange("tellus", e.target.value)
                  }
                  disabled={isLoading}
                  rows="4"
                />
              </div>

              {message && messageType === "error" && (
                <div className="error-message">{message}</div>
              )}

              <button
                type="submit"
                className="auth-button primary"
                disabled={isLoading}
              >
                {isLoading ? "Submitting..." : "Submit Enquiry"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnquiryModal;
